/**
 * Access / plan / billing view for a user (read-only diagnostics + existing
 * plan change / cancel actions).
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, Gauge, Receipt, ShoppingCart, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  cancelUserSubscription,
  changeUserPlan,
  getPlans,
  type AdminUserDetail,
  type PlanData,
} from '@/services/adminService';
import { Badge, InfoRow, Section, STATUS_COLORS, formatDate, type useFeedback } from './shared';

const ACCESS_STATUSES = new Set(['ACTIVE', 'TRIALING', 'PAST_DUE', 'active']);

const SEVERITY_STYLES: Record<string, string> = {
  alta: 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300',
  media: 'border-orange-300 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300',
  info: 'border-border bg-muted/50 text-muted-foreground',
};

function formatLimit(usage: number, limit: number) {
  return limit === -1 ? `${usage} / ∞` : `${usage} / ${limit}`;
}

interface AccessPanelProps {
  detail: AdminUserDetail;
  feedback: ReturnType<typeof useFeedback>;
  onChanged: () => void;
}

const AccessPanel: React.FC<AccessPanelProps> = ({ detail, feedback, onChanged }) => {
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [busy, setBusy] = useState(false);
  const { planStatus, subscriptions, invoices, onprofitEvents, diagnosticos } = detail;

  useEffect(() => {
    getPlans().then(({ plans }) => setPlans(plans)).catch(() => {});
  }, []);

  const handleChangePlan = async () => {
    const plan = plans.find((p) => p.id === selectedPlanId);
    if (!plan) return;
    if (!confirm(`Trocar o plano deste usuário para "${plan.name}"? A assinatura com acesso atual será cancelada.`)) return;
    setBusy(true);
    try {
      await changeUserPlan(detail.auth.id, plan.id);
      feedback.success(`Plano alterado para ${plan.name}.`);
      setSelectedPlanId('');
      onChanged();
    } catch (err) {
      feedback.error(err);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async (subscriptionId: string, planName: string) => {
    if (!confirm(`Cancelar a assinatura "${planName}"? O usuário perde o acesso imediatamente.`)) return;
    setBusy(true);
    try {
      await cancelUserSubscription(subscriptionId);
      feedback.success('Assinatura cancelada.');
      onChanged();
    } catch (err) {
      feedback.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section title="Diagnóstico" icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}>
        {diagnosticos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum problema detectado.</p>
        ) : (
          <div className="space-y-2">
            {diagnosticos.map((d) => (
              <div key={d.codigo + d.mensagem} className={cn('border rounded-lg p-2.5 text-sm', SEVERITY_STYLES[d.severidade])}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Badge label={d.severidade} className="bg-background/60" />
                  <code className="text-xs">{d.codigo}</code>
                </div>
                <p>{d.mensagem}</p>
              </div>
            ))}
          </div>
        )}
        {detail.partialErrors.length > 0 && (
          <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" /> Dados parciais: {detail.partialErrors.join('; ')}
          </p>
        )}
      </Section>

      <Section title="Plano e uso" icon={<Gauge className="h-4 w-4 text-muted-foreground" />}>
        {planStatus ? (
          <div className="grid grid-cols-2 gap-x-6">
            <InfoRow label="Plano efetivo" value={planStatus.planName} />
            <InfoRow
              label="Pode gerar carrossel"
              value={<Badge label={planStatus.canCreateCarousel ? 'Sim' : 'Não'} className={planStatus.canCreateCarousel ? STATUS_COLORS.ACTIVE : STATUS_COLORS.CANCELED} />}
            />
            <InfoRow label="Carrosséis (mês)" value={formatLimit(planStatus.usage.carousels, planStatus.limits.carousel_limit)} />
            <InfoRow label="Gerações (mês)" value={formatLimit(planStatus.usage.generations, planStatus.limits.generation_limit)} />
            {planStatus.limits.daily_carousel_limit !== null && (
              <InfoRow label="Carrosséis hoje" value={formatLimit(planStatus.dailyCarouselUsage, planStatus.limits.daily_carousel_limit)} />
            )}
            <InfoRow label="Trial usado (legado)" value={detail.profile?.trial_used ? 'Sim' : 'Não'} />
            <InfoRow label="paywall_active (legado)" value={detail.profile?.paywall_active ? 'Sim' : 'Não'} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Não foi possível calcular o status do plano.</p>
        )}
      </Section>

      <Section
        title={`Assinaturas (${subscriptions.length})`}
        icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
        actions={
          <>
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            >
              <option value="">Trocar plano…</option>
              {plans.filter((p) => p.isActive).map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.name} ({plan.slug})</option>
              ))}
            </select>
            <Button size="sm" variant="outline" className="h-8" disabled={!selectedPlanId || busy} onClick={handleChangePlan}>
              Aplicar
            </Button>
          </>
        }
      >
        {subscriptions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma assinatura.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left py-1 pr-2 font-medium">Plano</th>
                  <th className="text-left py-1 pr-2 font-medium">Status</th>
                  <th className="text-left py-1 pr-2 font-medium">Período</th>
                  <th className="text-left py-1 pr-2 font-medium">Início → Fim</th>
                  <th className="text-left py-1 pr-2 font-medium">Provedor / pedido</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="border-t border-border align-top">
                    <td className="py-1.5 pr-2">{sub.plan?.name ?? sub.plan_id}</td>
                    <td className="py-1.5 pr-2"><Badge label={sub.status} className={STATUS_COLORS[sub.status]} /></td>
                    <td className="py-1.5 pr-2">{sub.period ?? '—'}</td>
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      {formatDate(sub.current_period_start)} → {formatDate(sub.current_period_end)}
                      {sub.canceled_at && <div className="text-muted-foreground">cancelada {formatDate(sub.canceled_at)}</div>}
                    </td>
                    <td className="py-1.5 pr-2">
                      {sub.payment_provider ?? '—'}
                      {sub.external_subscription_id && <div className="text-muted-foreground font-mono">{sub.external_subscription_id}</div>}
                    </td>
                    <td className="py-1.5 text-right">
                      {ACCESS_STATUSES.has(sub.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-destructive"
                          disabled={busy}
                          onClick={() => handleCancel(sub.id, sub.plan?.name ?? sub.id)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Faturas" icon={<Receipt className="h-4 w-4 text-muted-foreground" />}>
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma fatura.</p>
        ) : (
          <div className="space-y-1">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground w-28 shrink-0">{formatDate(inv.created_at)}</span>
                <span className="flex-1">{inv.plan_name} · {inv.period}</span>
                <span>R$ {Number(inv.amount).toFixed(2)}</span>
                <Badge label={inv.status} className={STATUS_COLORS[inv.status]} />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Eventos OnProfit (por e-mail)" icon={<ShoppingCart className="h-4 w-4 text-muted-foreground" />}>
        {onprofitEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum evento com este e-mail.</p>
        ) : (
          <div className="space-y-1">
            {onprofitEvents.map((evt) => (
              <div key={`${evt.order_id}-${evt.created_at}`} className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground w-28 shrink-0">{formatDate(evt.created_at)}</span>
                <span className="font-mono">#{evt.order_id}</span>
                <span className="flex-1 text-muted-foreground">{evt.item_type ?? ''}</span>
                {evt.price_cents !== null && <span>R$ {(evt.price_cents / 100).toFixed(2)}</span>}
                <Badge label={evt.status ?? '—'} className={STATUS_COLORS[evt.status ?? '']} />
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default AccessPanel;
